import React from "react";
import { motion } from "framer-motion";
import { ListChecks, Sparkles, Repeat, Gift, ArrowRight, Bell, Clock, Check, Plus } from "lucide-react";

const PUBLIC_DEMO = "/book/ac9c3982-f96d-4fa3-91e3-cba7234c6e88";

/**
 * Second demo section on the Landing — focused on the "intelligence" features
 * that go beyond the basic calendar (waitlist, auto-fill, auto-book regulars,
 * loyalty + combo). Each card has a contextual CTA to a real demo entry point.
 */
export default function AppointmentIntelligenceDemos() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="appt-intelligence-section">
      <div className="max-w-2xl mb-10 md:mb-14">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-violet-600 mb-3 dark:text-violet-400">L'agenda lavora per te</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium dark:text-zinc-100">
          Riempie gli slot <em className="not-italic text-violet-600 dark:text-violet-400">mentre dormi</em>.
        </h2>
        <p className="text-sm md:text-base text-zinc-600 mt-3 dark:text-zinc-400 max-w-xl">
          4 automatismi che trasformano il tuo calendario in una macchina di fatturato.
          Niente da configurare, tutto già acceso.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
        <DemoWaitlist />
        <DemoAutoFill />
        <DemoAutoBook />
        <DemoLoyalty />
      </div>
    </section>
  );
}

const cardClass =
  "rounded-3xl bg-white border border-zinc-100 p-6 md:p-7 overflow-hidden relative dark:bg-zinc-950 dark:border-zinc-800";
const labelClass = "text-[10px] font-bold tracking-[0.2em] uppercase text-violet-600 dark:text-violet-400 mb-2 inline-flex items-center gap-1.5";
const titleClass = "font-display text-xl md:text-2xl font-medium tracking-tight dark:text-zinc-100";
const descClass = "text-sm text-zinc-600 mt-1.5 dark:text-zinc-400";

function CtaInline({ to, label }) {
  return (
    <a
      href={to}
      className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 transition-colors"
      data-testid="appt-cta-inline"
    >
      {label} <ArrowRight className="h-3 w-3" />
    </a>
  );
}

// ============================================================================
// DEMO 1 — Waitlist auto-notification when a slot frees up
// ============================================================================
function DemoWaitlist() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className={cardClass}
      data-testid="demo-waitlist"
    >
      <div className={labelClass}><ListChecks className="h-3 w-3" /> Lista d'attesa</div>
      <h3 className={titleClass}>Una cancellazione = una notifica.</h3>
      <p className={descClass}>Quando uno slot si libera, il sistema avvisa il primo cliente in lista d'attesa che ci entra. Tu non muovi un dito.</p>

      <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-4 ring-1 ring-zinc-100 dark:ring-zinc-800 space-y-2">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          className="rounded-xl bg-white dark:bg-zinc-950 px-3 py-2.5 ring-1 ring-zinc-100 dark:ring-zinc-800 flex items-center gap-3"
        >
          <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold dark:text-zinc-100">Ven 15 mag, 16:30 — annullato da Lucia</div>
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">Manicure classica · 45 min</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.45 }}
          className="flex justify-center"
        >
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600 dark:text-violet-400 inline-flex items-center gap-1">
            <Bell className="h-3 w-3 animate-pulse" /> Notifica automatica
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 dark:bg-emerald-950/30 dark:border-emerald-900/50 flex items-start gap-3"
        >
          <span className="h-7 w-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
            <Check className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-emerald-900 dark:text-emerald-200">Sara ha confermato lo slot</div>
            <div className="text-[10px] text-emerald-700 dark:text-emerald-300/80">3° in lista · era in attesa da 4 giorni</div>
          </div>
        </motion.div>
      </div>

      <CtaInline to="/demo#attesa" label="Prova la simulazione" />
    </motion.div>
  );
}

// ============================================================================
// DEMO 2 — Auto-fill suggestions: empty slot → ranked customer candidates
// ============================================================================
function DemoAutoFill() {
  const candidates = [
    { name: "Maria Rossi", reason: "Ricorrente · ogni 28g · scaduta", score: "alta" },
    { name: "Anna Bianchi", reason: "No-show ratio 0%", score: "alta" },
    { name: "Lucia Verdi", reason: "Ha fatto questo servizio 3 volte", score: "media" },
  ];
  const tone = (s) => ({
    alta: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
    media: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  }[s] || "bg-zinc-100 text-zinc-700");
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: 0.05 }}
      className={cardClass}
      data-testid="demo-autofill"
    >
      <div className={labelClass}><Sparkles className="h-3 w-3" /> Riempimento automatico</div>
      <h3 className={titleClass}>Slot vuoto domani? Ti dico chi chiamare.</h3>
      <p className={descClass}>Il sistema analizza lo storico e ti suggerisce i clienti più probabili da prenotare per ogni buco in agenda — ordinati per probabilità di accettazione.</p>

      <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-4 ring-1 ring-zinc-100 dark:ring-zinc-800">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 dark:text-zinc-400">Slot vuoto</div>
            <div className="font-medium text-sm dark:text-zinc-100 inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-violet-500" /> Mar 14:00 · Manicure</div>
          </div>
        </div>
        <ul className="space-y-1.5">
          {candidates.map((c, i) => (
            <motion.li
              key={c.name}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="rounded-xl bg-white dark:bg-zinc-950 px-3 py-2 ring-1 ring-zinc-100 dark:ring-zinc-800 flex items-center gap-2.5"
            >
              <span className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300 text-xs font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold dark:text-zinc-100 truncate">{c.name}</div>
                <div className="text-[10px] text-zinc-500 dark:text-zinc-400 truncate">{c.reason}</div>
              </div>
              <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full whitespace-nowrap ${tone(c.score)}`}>
                {c.score}
              </span>
            </motion.li>
          ))}
        </ul>
      </div>

      <CtaInline to="/demo#autofill" label="Esplora i suggerimenti" />
    </motion.div>
  );
}

// ============================================================================
// DEMO 3 — Auto-book regular customers: weekly cadence detected
// ============================================================================
function DemoAutoBook() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={cardClass}
      data-testid="demo-autobook"
    >
      <div className={labelClass}><Repeat className="h-3 w-3" /> Auto-prenotazione</div>
      <h3 className={titleClass}>Clienti abituali? Si prenotano da soli.</h3>
      <p className={descClass}>Marta viene ogni lunedì alle 18:00. Il sistema rileva il pattern e crea l'appuntamento automaticamente, anche se Marta dimentica di chiamare.</p>

      <div className="mt-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-4 ring-1 ring-zinc-100 dark:ring-zinc-800">
        <div className="rounded-xl bg-white dark:bg-zinc-950 px-3 py-2.5 ring-1 ring-zinc-100 dark:ring-zinc-800">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="text-xs font-semibold dark:text-zinc-100">Marta Bianchi</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full dark:text-amber-300 dark:bg-amber-950/50">
              <Sparkles className="h-2.5 w-2.5" /> Auto-book
            </span>
          </div>
          <div className="text-[10px] text-zinc-500 dark:text-zinc-400">Pattern rilevato · Pulizia viso · ogni 7 giorni · Lun 18:00</div>
          <div className="mt-2 text-[10px] text-zinc-700 dark:text-zinc-300 grid grid-cols-7 gap-0.5">
            {["L","M","M","G","V","S","D"].map((d, i) => (
              <div key={i} className={`h-7 rounded text-center pt-1 font-medium ${i === 0 ? "bg-violet-600 text-white" : "bg-zinc-100 dark:bg-zinc-800"}`}>{d}</div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-3 flex items-center justify-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-violet-600 dark:text-violet-400"
        >
          <Bell className="h-3 w-3" /> Lunedì alle 09:00 · Prenotazione creata
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="mt-3 rounded-xl bg-violet-50 border border-violet-200 px-3 py-2 dark:bg-violet-950/30 dark:border-violet-900/50 flex items-center gap-2.5"
        >
          <span className="h-6 w-6 rounded-full bg-violet-600 text-white flex items-center justify-center shrink-0">
            <Plus className="h-3.5 w-3.5" />
          </span>
          <div className="flex-1 min-w-0 text-[11px]">
            <div className="font-semibold text-violet-900 dark:text-violet-200">Lun 19 mag · 18:00</div>
            <div className="text-[10px] text-violet-700 dark:text-violet-300/80">Pulizia viso (45 min) · €40</div>
          </div>
        </motion.div>
      </div>

      <CtaInline to="/demo#autobook" label="Vedi auto-book in azione" />
    </motion.div>
  );
}

// ============================================================================
// DEMO 4 — Loyalty progress + combo multi-service booking
// ============================================================================
function DemoLoyalty() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: 0.15 }}
      className={cardClass}
      data-testid="demo-loyalty"
    >
      <div className={labelClass}><Gift className="h-3 w-3" /> Fedeltà & Combo</div>
      <h3 className={titleClass}>Premia chi torna. Vendi servizi insieme.</h3>
      <p className={descClass}>Programma fedeltà automatico (10 visite = sconto) e prenotazioni combo: il cliente sceglie più servizi consecutivi, il sistema trova lo slot in cui entrano tutti.</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Loyalty card */}
        <div className="rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-3 ring-1 ring-zinc-100 dark:ring-zinc-800">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-600 dark:text-emerald-400 mb-2">🎁 Programma fedeltà</div>
          <div className="text-xs font-semibold dark:text-zinc-100">Giulia Verdi</div>
          <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5">8 / 10 visite</div>
          <div className="mt-2 h-2 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "80%" }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.7, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600"
            />
          </div>
          <div className="text-[10px] mt-1.5 text-emerald-700 dark:text-emerald-400">Premio: Sconto 10% sulla prossima</div>
        </div>

        {/* Combo card */}
        <div className="rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 p-3 ring-1 ring-zinc-100 dark:ring-zinc-800">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600 dark:text-blue-400 mb-2">+ Combo prenotazione</div>
          <div className="space-y-1">
            {[
              { svc: "Manicure", t: "30 min", c: "bg-blue-500" },
              { svc: "Pedicure", t: "45 min", c: "bg-violet-500" },
              { svc: "Trattamento", t: "20 min", c: "bg-emerald-500" },
            ].map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.12 }}
                className="flex items-center gap-1.5 text-[10px]"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${row.c} shrink-0`} />
                <span className="font-medium dark:text-zinc-100 truncate">{row.svc}</span>
                <span className="text-zinc-400 dark:text-zinc-500 ml-auto tabular-nums">{row.t}</span>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            className="mt-2 pt-2 border-t border-zinc-200 dark:border-zinc-700 text-[10px] flex items-center justify-between"
          >
            <span className="text-zinc-500 dark:text-zinc-400">Totale 1h 35min</span>
            <span className="font-bold text-blue-600 dark:text-blue-400">€85</span>
          </motion.div>
        </div>
      </div>

      <CtaInline to="/demo#combo" label="Prova combo & fedeltà" />
    </motion.div>
  );
}
